/**
 * BCR artifacts panel — shows uploaded package names and Artifactory links.
 * Supports adding custom artifact packages via a "+" button.
 */

import { useState } from 'react';
import { Card, Table, Typography, Button, Modal, Form, Input, Select, message, Space } from 'antd';
import { InboxOutlined, LinkOutlined, PlusOutlined, DeleteOutlined, BgColorsOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { BcrArtifactItem } from '@/types';
import { useMemo } from 'react';
import { type ArtifactRow, buildCustomRows, mergeArtifactRows } from './artifactDedup';

const { Link, Text } = Typography;

interface BcrArtifactsProps {
  uploadedArtifacts?: Record<string, BcrArtifactItem>;
  customArtifacts?: Array<{ kind: string; name: string; url?: string }>;
  onGenerateArtifacts?: () => Promise<void>;
  isGenerating?: boolean;
  onCustomArtifactsChange?: (customArtifacts: ArtifactRow[]) => void;
}

const ARTIFACT_TYPES = [
  { label: 'MF', value: 'MF' },
  { label: 'BSW', value: 'BSW' },
  { label: 'BSW-SOC', value: 'BSW-SOC' },
  { label: 'MCU', value: 'MCU' },
  { label: 'SWITCH', value: 'SWITCH' },
  { label: '其他', value: 'OTHER' },
];

const BcrArtifacts: React.FC<BcrArtifactsProps> =({ uploadedArtifacts, customArtifacts: initialCustomArtifacts, onGenerateArtifacts, isGenerating, onCustomArtifactsChange }) => {
  const [form] = Form.useForm();
  const [modalVisible, setModalVisible] = useState(false);

  // 从 prop 派生 UI 数据（受控组件方式），确保异步加载的 prop 也能正确渲染
  const customArtifactRows: ArtifactRow[] = useMemo(
    () => buildCustomRows(initialCustomArtifacts),
    [initialCustomArtifacts],
  );

  const handleAddArtifact = async () => {
    try {
      const values = await form.validateFields();
      const newArtifact: ArtifactRow = {
        key: `custom-${Date.now()}`,
        kind: values.artifactType,
        rmp_kind: values.artifactType,
        name: values.packageName,
        url: values.packageUrl || '',
        isCustom: true,
      };
      const updated = [...customArtifactRows, newArtifact];
      if (onCustomArtifactsChange) {
        await onCustomArtifactsChange(updated);
      }
      form.resetFields();
      setModalVisible(false);
      message.success('制品包已添加');
    } catch (error) {
      // Form validation failed or API error
      message.error('添加失败，请重试');
    }
  };

  const handleDeleteCustom = async (key: string) => {
    const updated = customArtifactRows.filter((item) => item.key !== key);
    if (onCustomArtifactsChange) {
      try {
        await onCustomArtifactsChange(updated);
      } catch {
        message.error('删除失败，请重试');
      }
    }
  };

  const columns: ColumnsType<ArtifactRow> = [
    {
      title: '产物类型',
      dataIndex: 'rmp_kind',
      width: 100,
    },
    {
      title: '包名',
      dataIndex: 'name',
      render: (name: string) => (
        <Text style={{ wordBreak: 'break-all' }}>{name}</Text>
      ),
    },
    {
      title: '制品仓链接',
      dataIndex: 'url',
      width: 100,
      render: (url: string) =>
        url ? (
          <Link href={url} target="_blank" rel="noopener noreferrer">
            <LinkOutlined /> 查看
          </Link>
        ) : (
          '-'
        ),
    },
    {
      title: '操作',
      width: 80,
      render: (_, record) =>
        record.isCustom ? (
          <Button
            type="link"
            danger
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteCustom(record.key)}
          >
            删除
          </Button>
        ) : null,
    },
  ];

  // 归并语义（类型规范化 + 构建产物优先）见 artifactDedup.ts，改动必须先改其单测
  const allRows: ArtifactRow[] = useMemo(
    () => mergeArtifactRows(uploadedArtifacts, initialCustomArtifacts),
    [uploadedArtifacts, initialCustomArtifacts],
  );

  return (
    <>
      <Card
        title={<><InboxOutlined /> 制品包</>}
        extra={
          <Space size="small">
            {!uploadedArtifacts && onGenerateArtifacts && (
              <Button
                type="primary"
                size="small"
                icon={<BgColorsOutlined />}
                loading={isGenerating}
                onClick={onGenerateArtifacts}
              >
                生成
              </Button>
            )}
            {onCustomArtifactsChange && (
              <Button
                type="link"
                size="small"
                icon={<PlusOutlined />}
                onClick={() => setModalVisible(true)}
              >
                添加
              </Button>
            )}
          </Space>
        }
        className="mb-4"
      >
        {allRows.length === 0 ? (
          <div style={{ color: '#999', textAlign: 'center', padding: '20px 0' }}>
            {onCustomArtifactsChange ? '暂无制品包信息，点击"添加"开始记录' : '暂无制品包信息'}
          </div>
        ) : (
          <Table
            size="small"
            dataSource={allRows}
            columns={columns}
            rowKey="key"
            pagination={false}
          />
        )}
      </Card>

      <Modal
        title="添加制品包"
        open={modalVisible}
        onOk={handleAddArtifact}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        okText="添加"
        cancelText="取消"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="产物类型"
            name="artifactType"
            rules={[{ required: true, message: '请选择产物类型' }]}
          >
            <Select
              placeholder="选择产物类型"
              options={ARTIFACT_TYPES}
            />
          </Form.Item>
          <Form.Item
            label="包名"
            name="packageName"
            rules={[{ required: true, message: '请输入包名' }]}
          >
            <Input placeholder="输入包文件名称" />
          </Form.Item>
          <Form.Item
            label="制品仓链接"
            name="packageUrl"
            rules={[
              {
                type: 'url',
                message: '请输入有效的URL',
              },
            ]}
          >
            <Input placeholder="输入链接地址（可选）" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default BcrArtifacts;
